/**
 * The site's sections.
 *
 * Every route this site has was reachable only by already knowing an address —
 * a block hash, a preimage, an SS58 string, a spec_version. That is fine for a
 * link somebody sent you and useless for finding out the pages exist at all.
 * This is the answer to "what else is here".
 *
 * The standings are the first entry because they are the front page, and there
 * is no "Miners" beside them for the same reason: the standings already are the
 * index of miners, and a second page listing the same rows under a different
 * address would be two answers to one question.
 */

import { Link } from 'react-router-dom'

import type { Window as WindowName } from '../api/generated/Window'
import { href, isStandings, type Route, SECTIONS, sectionOf } from '../lib/routes'

export function SectionNav({
  chain,
  route,
  window: windowName,
}: {
  chain: string | null
  route: Route
  /** Carried into the standings link so leaving a section and coming back does
   *  not silently reset the span someone chose. */
  window: WindowName
}) {
  if (!chain) return null
  // The standings are current when nothing else is: no panel open, no index.
  const onStandings = isStandings(route)
  const current = sectionOf(route)

  return (
    <nav className="sections" aria-label="Section">
      <Link
        to={href({ chain, window: windowName })}
        className="section-link"
        aria-current={onStandings ? 'page' : undefined}
      >
        Standings
      </Link>
      {/* Straight to block zero. Endowments happen there and are invisible to
          anyone who has not read the chainspec, so somebody who would never
          think to look is given an unmissable way to. Not a `SECTIONS` entry
          because it is not an index — it is one particular block. */}
      <Link
        to={href({ chain, block: '0' })}
        className="section-link"
        aria-current={route.block === '0' ? 'page' : undefined}
      >
        Genesis
      </Link>
      {SECTIONS.filter((s) => !s.header).map((s) => (
        <Link
          key={s.id}
          to={href({ chain, index: s.id })}
          className="section-link"
          // A subject's own page belongs to the section that indexes it.
          aria-current={current === s.id ? 'page' : undefined}
        >
          {s.label}
        </Link>
      ))}
    </nav>
  )
}
